import React, { useState } from "react";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";

export type ProfileOverviewSectionVariant = "member" | "staff";

interface ProfileOverviewSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  /** Renders between the title and the chevron (e.g. Edit Profile + pencil). Clicks should not toggle the section. */
  headerActions?: React.ReactNode;
  /** Member site = red headers; staff shell = blue (`secondary`). */
  variant?: ProfileOverviewSectionVariant;
}

const ProfileOverviewSection: React.FC<ProfileOverviewSectionProps> = ({
  title,
  defaultOpen = true,
  children,
  headerActions,
  variant = "member",
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => setOpen((o) => !o);

  const headerBg = variant === "staff" ? "bg-secondary" : "bg-primary";
  const headerHover = variant === "staff" ? "hover:bg-secondary/95" : "hover:bg-primary/95";
  const ringOffset =
    variant === "staff"
      ? "focus-visible:ring-offset-secondary"
      : "focus-visible:ring-offset-primary";
  const chevronBg = variant === "staff" ? "bg-secondary-dark" : "bg-primary-dark";
  const chevronHover =
    variant === "staff" ? "hover:bg-secondary-dark/95" : "hover:bg-primary-dark/95";
  const chevronRingOffset =
    variant === "staff"
      ? "focus-visible:ring-offset-secondary-dark"
      : "focus-visible:ring-offset-primary-dark";

  return (
    <div className="mb-3 overflow-hidden rounded-none shadow-sm">
      <div
        className={`grid w-full items-stretch overflow-hidden ${headerBg} ${
          headerActions
            ? "grid-cols-[minmax(0,1fr)_auto_auto]"
            : "grid-cols-[minmax(0,1fr)_auto]"
        }`}
      >
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className={`flex min-w-0 items-center px-6 py-4 text-left text-white transition md:px-8 md:py-5 ${headerHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 ${ringOffset}`}
        >
          <h3 className="min-w-0 truncate font-decimal text-xl font-normal uppercase leading-normal tracking-wide md:text-[23px] 2xl:text-[24px]">
            {title}
          </h3>
        </button>
        {headerActions ? (
          <div
            className="flex items-center justify-end px-4 py-3 md:px-6 md:py-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            {headerActions}
          </div>
        ) : null}
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className={`flex w-14 flex-col items-center justify-center justify-self-stretch ${chevronBg} px-4 py-4 md:py-5 text-white transition ${chevronHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 ${chevronRingOffset}`}
          aria-label={open ? "Collapse section" : "Expand section"}
        >
          <span
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          >
            <ChevronDown className="block h-6 w-6" />
          </span>
        </button>
      </div>
      {open ? (
        <div className="border-t border-gray-100 bg-white px-4 py-6 md:px-6 md:py-8 lg:p-8">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default ProfileOverviewSection;
